import { useCallback, useMemo, useRef, useState } from "react";

export type SortDir = "asc" | "desc";
export type TableSort<K extends string> = { key: K; dir: SortDir } | null;

/** What a column sorts by: text, a number, a date (ISO string or Date), or nothing. */
export type SortValue = string | number | Date | null | undefined;

/**
 * Column sorting for a table. `toggle(key)` is wired to `SortableTableHead`:
 * a new column starts in its natural direction (`firstDir`, ascending unless
 * told otherwise — dates and counts usually read best newest/largest first),
 * and clicking the active column flips it.
 *
 * For a list the browser holds in full, `sortRows` applies the sort. A list
 * the server pages sends `sort` to the API instead, so the order holds across
 * every page rather than only the rows already loaded.
 */
export function useTableSort<K extends string>(
  initial: TableSort<K> = null,
  firstDir: Partial<Record<K, SortDir>> = {},
) {
  const [sort, setSort] = useState<TableSort<K>>(initial);
  // A static per-table map, usually an inline literal: held in a ref so
  // `toggle` stays stable rather than changing identity every render.
  const firstDirRef = useRef(firstDir);
  firstDirRef.current = firstDir;

  const toggle = useCallback(
    (key: K) =>
      setSort((cur) =>
        cur?.key === key
          ? { key, dir: cur.dir === "asc" ? "desc" : "asc" }
          : { key, dir: firstDirRef.current[key] ?? "asc" },
      ),
    [],
  );

  return { sort, setSort, toggle };
}

/**
 * Sorts a copy of `rows` by `sort`, reading each column through `getters`.
 * Text compares case-insensitively and numerically ("Item 2" before "Item 10");
 * empty values always sink to the bottom, whichever way the column runs.
 */
export function sortRows<T, K extends string>(
  rows: readonly T[],
  sort: TableSort<K>,
  getters: Record<K, (row: T) => SortValue>,
): T[] {
  if (!sort) return [...rows];
  const get = getters[sort.key];
  const sign = sort.dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const x = normalize(get(a));
    const y = normalize(get(b));
    if (x === null || y === null) return x === y ? 0 : x === null ? 1 : -1;
    if (typeof x === "number" && typeof y === "number") return (x - y) * sign;
    return collator.compare(String(x), String(y)) * sign;
  });
}

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

function normalize(v: SortValue): string | number | null {
  if (v === null || v === undefined || v === "") return null;
  if (v instanceof Date) return v.getTime();
  return v;
}

/** `useMemo` wrapper around {@link sortRows} for the common case. */
export function useSortedRows<T, K extends string>(
  rows: readonly T[],
  sort: TableSort<K>,
  getters: Record<K, (row: T) => SortValue>,
): T[] {
  // `getters` is usually an inline object literal; the sort only needs to
  // re-run when the rows or the chosen column change.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  return useMemo(() => sortRows(rows, sort, getters), [rows, sort]);
}
